import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
// Import Helpers
import { genericMemo } from 'services/Helpers';
// Import UI Components
import { SkeletonLoader, Tooltip } from '@geneui/components';

export interface KeyValueItemBaseProps<T extends boolean | undefined> {
    isLoading?: boolean;
    value: React.ReactNode | string;
    className?: string;
    classNames: {
        pair: string;
        key: string;
        value: string;
        skeleton: string;
    };
    withLabelTooltip?: boolean;
    labelTooltipText?: string;
    isTranslated?: T;
    label: T extends true ? React.ReactNode : string;
}

const KeyValueItemBase = <T extends boolean>(props: KeyValueItemBaseProps<T>) => {
    const {
        label,
        value,
        isLoading,
        className,
        classNames,
        withLabelTooltip,
        labelTooltipText,
        isTranslated,
        ...rest
    } = props;
    const { t } = useTranslation();
    const valueTextRef = useRef<HTMLParagraphElement>(null);
    const [isTooltipActive, setIsTooltipActive] = useState(false);

    useEffect(() => {
        const el = valueTextRef.current;

        if (el) {
            setIsTooltipActive(el.scrollWidth > el.clientWidth);
        }
    }, [value]);

    const valueContent = useMemo(() => {
        const valueParagraph = (
            <p ref={valueTextRef} className={classNames.value}>
                {value}
            </p>
        );
        if (isTooltipActive) {
            return <Tooltip text={value as string}>{valueParagraph}</Tooltip>;
        }
        return valueParagraph;
    }, [classNames.value, isTooltipActive, value]);

    return (
        <div className={classnames(classNames.pair, className)} {...rest}>
            {withLabelTooltip ? (
                <Tooltip text={labelTooltipText}>
                    <p className={classNames.key}>{isTranslated ? label : t(label as string)}</p>
                </Tooltip>
            ) : (
                <p className={classNames.key}>{isTranslated ? label : t(label as string)}</p>
            )}
            <div className={classNames.skeleton}>
                <SkeletonLoader isBusy={isLoading}>{!isLoading && valueContent}</SkeletonLoader>
            </div>
        </div>
    );
};

export default genericMemo(KeyValueItemBase);
